import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { TabBar } from "./components/TabBar";
import { AuthProvider, useAuth } from "./lib/auth";
import { applyTheme } from "./lib/theme";
import { Duels } from "./screens/Duels";
import { Focus } from "./screens/Focus";
import { Grow } from "./screens/Grow";
import { Home } from "./screens/Home";
import { Lesson } from "./screens/Lesson";
import { Strategies } from "./screens/Strategies";
import { Login } from "./screens/Login";
import { Me } from "./screens/Me";
import { Play } from "./screens/Play";
import { Profile } from "./screens/Profile";
import { Ranks } from "./screens/Ranks";
// Fonts are bundled, not fetched from Google: faster, private, and works offline.
import "@fontsource/instrument-sans/400.css";
import "@fontsource/instrument-sans/500.css";
import "@fontsource/instrument-sans/600.css";
import "@fontsource/instrument-sans/700.css";
import "./styles/tokens.css";
import "./styles/app.css";

applyTheme();

/** Signed-in pages with the tab bar. */
function WithTabs({ children }: { children: React.ReactNode }) {
  return <>{children}<TabBar /></>;
}

function App() {
  const { ready, user } = useAuth();
  const loc = useLocation();
  if (!ready) return null;
  if (!user) return <Routes><Route path="/join" element={<Login />} /><Route path="*" element={<Login />} /></Routes>;
  return <Routes>
    <Route path="/" element={<WithTabs><Home /></WithTabs>} />
    <Route path="/duels" element={<WithTabs><Duels /></WithTabs>} />
    <Route path="/ranks" element={<WithTabs><Ranks /></WithTabs>} />
    <Route path="/grow" element={<WithTabs><Grow /></WithTabs>} />
    <Route path="/learn" element={<Strategies />} />
    <Route path="/learn/:key" element={<Lesson />} />
    <Route path="/stats" element={<WithTabs><Profile self /></WithTabs>} />
    <Route path="/me" element={<WithTabs><Me /></WithTabs>} />
    <Route path="/u/:username" element={<WithTabs><Profile key={loc.pathname} /></WithTabs>} />
    <Route path="/focus" element={<Focus />} />
    {/* key: "Go again" re-mounts Play for a fresh session */}
    <Route path="/play" element={<Play key={loc.key} />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode><BrowserRouter><AuthProvider><App /></AuthProvider></BrowserRouter></StrictMode>,
);
